import api from './api';
import { Service } from './serviceApi';

export interface Pack {
  id: string;
  name: string;
  description?: string;
  total_price: number;
  payment_facility?: string;
  is_active: boolean;
  services: Service[];
  price_display: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePackData {
  name: string;
  description?: string;
  total_price: number;
  payment_facility?: string;
  service_ids: number[];
  is_active?: boolean;
}

export interface UpdatePackData extends Partial<CreatePackData> {}

class PackApiService {
  /**
   * Récupère tous les packs
   */
  async getPacks(isActive?: boolean): Promise<Pack[]> {
    try {
      const params = isActive !== undefined ? { is_active: isActive } : {};
      const response = await api.get('/api/packs/', { params });
      
      if (response.data.results) {
        return response.data.results;
      }
      
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Erreur lors de la récupération des packs:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des packs');
    }
  }

  /**
   * Récupère un pack par son ID
   */
  async getPack(id: string): Promise<Pack> {
    try {
      const response = await api.get(`/api/packs/${id}/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération du pack:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération du pack');
    }
  }

  /**
   * Crée un nouveau pack
   */
  async createPack(packData: CreatePackData): Promise<Pack> {
    try {
      const response = await api.post('/api/packs/', packData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création du pack:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        const errors = Object.values(error.response.data).flat();
        throw new Error(errors.join(', '));
      }
      throw new Error('Erreur lors de la création du pack');
    }
  }

  /**
   * Met à jour un pack
   */
  async updatePack(id: string, packData: UpdatePackData): Promise<Pack> {
    try {
      const response = await api.patch(`/api/packs/${id}/`, packData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du pack:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        const errors = Object.values(error.response.data).flat();
        throw new Error(errors.join(', '));
      }
      throw new Error('Erreur lors de la mise à jour du pack');
    }
  }

  /**
   * Supprime un pack
   */
  async deletePack(id: string): Promise<void> {
    try {
      await api.delete(`/api/packs/${id}/`);
    } catch (error: any) {
      console.error('Erreur lors de la suppression du pack:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la suppression du pack');
    }
  }

  /**
   * Active/désactive un pack
   */
  async togglePackStatus(id: string): Promise<Pack> {
    try {
      const response = await api.post(`/api/packs/${id}/toggle-status/`);
      return response.data.pack;
    } catch (error: any) {
      console.error('Erreur lors du changement de statut:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du changement de statut');
    }
  }

  /**
   * Valide les données d'un pack
   */
  validatePackData(data: CreatePackData | UpdatePackData): string[] {
    const errors: string[] = [];

    if ('name' in data && (!data.name || data.name.trim().length === 0)) {
      errors.push('Le nom du pack est requis');
    }

    if ('total_price' in data && (data.total_price === undefined || data.total_price < 0)) {
      errors.push('Le prix total doit être un nombre positif');
    }

    if ('service_ids' in data && (!data.service_ids || data.service_ids.length === 0)) {
      errors.push('Au moins un service doit être sélectionné');
    }

    return errors;
  }
}

export const packApiService = new PackApiService();
export default packApiService;
